/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Link } from '@fluentui/react/lib/Link';
import { Stack } from '@fluentui/react/lib/Stack';
import { getId } from '@fluentui/react/lib/Utilities';
import * as React from 'react';
import { scenarioMessages } from 'src/appShell/homePage/homePageScenariosMessages';
import {
    Bullet,
    HalfWidthStack,
    ScenarioBullet,
    ScenarioDescription,
    scenarioImageClassName,
    ScenarioQuote,
    ScenarioSection,
    ScenariosRootContainer,
    scenariosTheme,
    ScenariosTitle
} from 'src/appShell/homePage/homePageScenariosStyles';
import { LuisText } from 'src/core/components/LuisText';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useResponsiveMode } from 'src/core/hooks/useResponsiveMode';
import { getStylist } from 'src/core/themes/luisStylist';
import { getAssetsUrl } from 'src/core/utils/api/getAssetsUrl';
import { globalMessages } from 'src/core/utils/globalMessages';

type ScenariosSectionInfo = {
    /**
     * The scenario title.
     */
    title: ReactIntl.FormattedMessage.MessageDescriptor;

    /**
     * The scenario description.
     */
    description: ReactIntl.FormattedMessage.MessageDescriptor;

    /**
     * The scenario customer quote.
     */
    quote: ReactIntl.FormattedMessage.MessageDescriptor;

    /**
     * The scenario customer name.
     */
    customer: ReactIntl.FormattedMessage.MessageDescriptor;

    /**
     * The scenario customer learn more link text.
     */
    deepLinkText: ReactIntl.FormattedMessage.MessageDescriptor;

    /**
     * The scenario bullet points.
     */
    points: ReactIntl.FormattedMessage.MessageDescriptor[];

    /**
     * The scenario customer learn more link href.
     */
    deepLinkHref: string;

    /**
     * The scenario main learn more link.
     */
    learnMoreLink: string;

    /**
     * The scenario learn more link aria label.
     */
    learnMoreLinkAriaLabel: ReactIntl.FormattedMessage.MessageDescriptor;

    /**
     * The scenario image.
     */
    imageSrc: string;

    /**
     * The scenario customer icon image.
     */
    iconSrc: string;

    /**
     * The scenario customer icon width
     */
    iconWidth: string;
};

const { styleComponent } = getStylist('HomePageScenarios');
const ScenarioLink = styleComponent(Link)('scenarioLink', theme => ({ color: theme.linkColor, textDecoration: 'underline' }));

/**
 * Renders the scenarios section in the home page.
 */
export const HomePageScenarios = () => {
    const { localize } = useLocalization();
    const { isXLargeOrGreater } = useResponsiveMode();

    return (
        <ScenariosRootContainer role="region" customProps={{ isLargeScreen: isXLargeOrGreater }}>
            <Stack horizontalAlign="center">
                <ScenariosTitle variant="xxLarge" as="h3" block>
                    {localize(scenarioMessages.mainTitle)}
                </ScenariosTitle>

                {scenariosInfo.map(scenario => (
                    <ScenarioSection key={getId()} tokens={{ childrenGap: 40 }} horizontalAlign="center">
                        <ScenarioLink {...{ href: scenario.learnMoreLink, target: '_blank', rel: 'noopener noreferrer' }}>
                            <LuisText variant="xxLarge" as="h4" fontWeight={400} block>
                                {localize(scenario.title)}
                            </LuisText>
                        </ScenarioLink>

                        <ScenarioDescription block>
                            {localize(scenario.description)}
                            &nbsp;
                            <ScenarioLink
                                {...{
                                    target: '_blank',
                                    rel: 'noopener noreferrer',
                                    href: scenario.learnMoreLink,
                                    'aria-label': localize(scenario.learnMoreLinkAriaLabel)
                                }}>
                                <LuisText fontWeight={500}>{localize(globalMessages.learnMore)}</LuisText>
                            </ScenarioLink>
                        </ScenarioDescription>

                        <img
                            className={scenarioImageClassName}
                            alt={localize(scenario.title)}
                            src={getAssetsUrl(`assets/images/homePage/${scenario.imageSrc}`)}
                        />

                        <Stack horizontal={isXLargeOrGreater} tokens={{ childrenGap: 15 }}>
                            <HalfWidthStack customProps={{ isLargeScreen: isXLargeOrGreater }} tokens={{ childrenGap: 15 }}>
                                {scenario.points.slice(0, 4).map((point, i) => renderBulletPoint(i, localize(point)))}
                            </HalfWidthStack>

                            <HalfWidthStack customProps={{ isLargeScreen: isXLargeOrGreater }} tokens={{ childrenGap: 15 }}>
                                {scenario.points.slice(4).map((point, i) => renderBulletPoint(i + 4, localize(point)))}
                            </HalfWidthStack>
                        </Stack>

                        <ScenarioQuote variant="xLarge">{localize(scenario.quote)}</ScenarioQuote>

                        <Stack horizontal horizontalAlign="center" tokens={{ childrenGap: 20 }} verticalAlign="center">
                            <img
                                width={scenario.iconWidth}
                                src={getAssetsUrl(`assets/images/homePage/${scenario.iconSrc}`)}
                                alt={localize(scenario.customer)}
                            />
                            <LuisText fontWeight={500}>{localize(scenario.customer)}</LuisText>
                        </Stack>

                        <ScenarioLink {...{ href: scenario.deepLinkHref, target: '_blank' }}>
                            <LuisText fontWeight={500}>{localize(scenario.deepLinkText)}</LuisText>
                        </ScenarioLink>
                    </ScenarioSection>
                ))}
            </Stack>
        </ScenariosRootContainer>
    );
};

/**
 * Renders a bullet point with the given index and text.
 *
 * @param index The bullet point index.
 * @param text The bullet point text.
 * @returns A JSX Element of the bullet.
 */
const renderBulletPoint = (index: number, text: string) => (
    <Stack key={getId()} horizontal tokens={{ childrenGap: 20 }} verticalAlign="center">
        <Bullet verticalAlign="center" horizontalAlign="center">
            <LuisText fontWeight={600}>{index + 1}</LuisText>
        </Bullet>

        <ScenarioBullet>{text}</ScenarioBullet>
    </Stack>
);

const scenariosInfo: ScenariosSectionInfo[] = [
    {
        title: scenarioMessages.informationChatBotTitle,
        description: scenarioMessages.informationChatBotDescription,
        quote: scenarioMessages.informationChatBotQuote,
        customer: scenarioMessages.informationChatBotCustomer,
        deepLinkText: scenarioMessages.informationChatBotDeepLink,
        learnMoreLink: 'https://docs.microsoft.com/bot-framework/bot-service-scenario-informational',
        learnMoreLinkAriaLabel: scenarioMessages.learnMoreChatBot,
        deepLinkHref: 'https://customers.microsoft.com/story/ups',
        iconSrc: 'upsLogo.png',
        iconWidth: scenariosTheme.iconWidthSmall,
        imageSrc: 'informationChatBot.svg',
        points: [
            scenarioMessages.informationChatBotBullet1,
            scenarioMessages.informationChatBotBullet2,
            scenarioMessages.informationChatBotBullet3,
            scenarioMessages.informationChatBotBullet4,
            scenarioMessages.informationChatBotBullet5,
            scenarioMessages.informationChatBotBullet6,
            scenarioMessages.informationChatBotBullet7
        ]
    },
    {
        title: scenarioMessages.iotDevicesTitle,
        description: scenarioMessages.iotDevicesDescription,
        quote: scenarioMessages.iotDevicesQuote,
        customer: scenarioMessages.iotDevicesCustomer,
        deepLinkText: scenarioMessages.iotDevicesDeepLink,
        learnMoreLink: 'https://docs.microsoft.com/bot-framework/bot-service-scenario-internet-things',
        learnMoreLinkAriaLabel: scenarioMessages.learnMoreIot,
        deepLinkHref: 'https://customers.microsoft.com/story/rockwell-automation',
        iconSrc: 'rockwellAutomationLogo.png',
        iconWidth: scenariosTheme.iconWidthLarge,
        imageSrc: 'iotDevices.svg',
        points: [
            scenarioMessages.iotDevicesBullet1,
            scenarioMessages.iotDevicesBullet2,
            scenarioMessages.iotDevicesBullet3,
            scenarioMessages.iotDevicesBullet4,
            scenarioMessages.iotDevicesBullet5
        ]
    },
    {
        title: scenarioMessages.commerceChatBotTitle,
        description: scenarioMessages.commerceChatBotDescription,
        quote: scenarioMessages.commerceChatBotQuote,
        customer: scenarioMessages.commerceChatBotCustomer,
        deepLinkText: scenarioMessages.commerceChatBotDeepLink,
        learnMoreLink: 'https://docs.microsoft.com/bot-framework/bot-service-scenario-commerce',
        learnMoreLinkAriaLabel: scenarioMessages.learnMoreCommerceChatBot,
        deepLinkHref: 'https://customers.microsoft.com/story/dixons-carphone-retail-cognitive-services',
        iconSrc: 'dixonsCarPhoneLogo.png',
        iconWidth: scenariosTheme.iconWidthLarge,
        imageSrc: 'commerceChatBot.svg',
        points: [
            scenarioMessages.commerceChatBotBullet1,
            scenarioMessages.commerceChatBotBullet2,
            scenarioMessages.commerceChatBotBullet3,
            scenarioMessages.commerceChatBotBullet4,
            scenarioMessages.commerceChatBotBullet5,
            scenarioMessages.commerceChatBotBullet6,
            scenarioMessages.commerceChatBotBullet7
        ]
    }
];
